/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import PrimaryButton from '../PrimaryButton';
import SelectCard from './SelectCard';
import AppIcon from './AppIcon';
import { colors, radii, spacing, typography } from '../../config/theme';
import { CITY_LIST_LABEL } from '../../config/onboardingData';
import { searchCities } from '../../services/city-service';
//import { getOnboardingCities } from '../../services/cities-api';
import { getOnboardingCities, getCachedCities } from '../../services/cities-api';

// Waits until the user stops typing before returning the new value.
// This stops us searching on every single keystroke.
const useDebouncedValue = (value, delay = 250) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

// A small map-pin drawn with Views, so it can be tinted (an emoji can't be).
const PinIcon = ({ color, dot }) => (
  <View style={[styles.pin, { backgroundColor: color }]}>
    <View style={[styles.pinDot, { backgroundColor: dot }]} />
  </View>
);

// Grey placeholder rows shown while the cities load.
const SkeletonRow = () => (
  <View style={styles.skeletonCard}>
    <View style={styles.skeletonIcon} />
    <View style={styles.skeletonText}>
      <View style={[styles.skeletonLine, { width: '45%' }]} />
      <View style={[styles.skeletonLine, { width: '70%', height: 10 }]} />
    </View>
  </View>
);

// Props:
//   visible, onClose
//   initialCity     the city that starts selected (a city object), or null
//   onSelectCity(city)   called with the chosen city when the CTA is pressed
//   onUseGps()           called when the GPS chip is pressed
//
// The suggested list comes from GET /v1/onboarding/cities. Typing filters that
// list and also asks the city search (city-service) for more matches.
const CitySearchSheet = ({
  visible,
  onClose,
  initialCity,
  onSelectCity,
  onUseGps,
}) => {
  const { t, i18n } = useTranslation();
  const insets = useSafeAreaInsets();
  const language =
    i18n.language === 'dt' ? 'du' : (i18n.language || 'en').split('-')[0];

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 250);

  // The cities from the API. status: 'loading' | 'ready' | 'error'
  const [api, setApi] = useState(() => {
    const cached = getCachedCities(language);
    return cached
      ? { status: 'ready', list: cached, message: '' }
      : { status: 'loading', list: [], message: '' };
  });
  // Extra matches from the search while the user is typing.
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [selectedId, setSelectedId] = useState(initialCity?.id || null);
  const latestRequest = useRef(0);

  const loadCities = useCallback(
    force => {
      setApi(prev => ({ ...prev, status: 'loading', message: '' }));
      getOnboardingCities({ force, language })
        .then(list => setApi({ status: 'ready', list, message: '' }))
        .catch(e =>
          setApi({ status: 'error', list: [], message: e?.message || '' }),
        );
    },
    [language],
  );

  // Load the list the first time the sheet opens (and again if it failed).
  useEffect(() => {
    if (visible && api.status !== 'ready') {
      loadCities(false);
    }
  }, [visible]);

  // When the sheet opens, start from the city chosen before (if any).
  useEffect(() => {
    if (visible) {
      setSelectedId(initialCity?.id || null);
    } else {
      // clear the search text shortly after closing (after the slide-out)
      const id = setTimeout(() => setQuery(''), 400);
      return () => clearTimeout(id);
    }
  }, [visible, initialCity?.id]);

  // While typing, also ask the city search for more matches.
  useEffect(() => {
    const q = debouncedQuery.trim();
    if (!visible || !q) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    const requestId = ++latestRequest.current;
    setSearching(true);
    searchCities(q)
      .then(list => {
        // ignore the answer if the user has already typed something newer
        if (requestId === latestRequest.current) setSearchResults(list || []);
      })
      .catch(() => {
        if (requestId === latestRequest.current) setSearchResults([]);
      })
      .finally(() => {
        if (requestId === latestRequest.current) setSearching(false);
      });
  }, [debouncedQuery, visible]);

  const typing = query.trim().length > 0;

  // What the list shows:
  //   nothing typed -> the API cities
  //   typing        -> API cities that match, then the search matches
  //                    (the same city is never listed twice)
  const cities = useMemo(() => {
    if (!typing) {
      return api.list;
    }
    const q = query.trim().toLowerCase();
    const fromApi = api.list.filter(
      c =>
        c.name.toLowerCase().includes(q) || c.region.toLowerCase().includes(q),
    );
    const seen = new Set(fromApi.map(c => c.name.toLowerCase()));
    const extra = searchResults.filter(c => !seen.has(c.name.toLowerCase()));
    return [...fromApi, ...extra];
  }, [typing, query, api.list, searchResults]);

  // While typing, the top match is selected unless the user chose another one.
  useEffect(() => {
    if (typing && cities.length > 0 && !cities.some(c => c.id === selectedId)) {
      setSelectedId(cities[0].id);
    }
  }, [typing, cities]);
  useEffect(() => {
    // warm the cache so the city sheet opens with the list already loaded
    getOnboardingCities({ language }).catch(() => { });
  }, []);

  const selected = cities.find(c => c.id === selectedId) || null;

  const showSkeleton = !typing && api.status === 'loading';
  const showError = !typing && api.status === 'error';
  const busy = (typing && searching) || (!typing && api.status === 'loading');
  const showEmpty =
    !busy && !showError && !showSkeleton && cities.length === 0;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose} // Android back button
      statusBarTranslucent>
      <KeyboardAvoidingView
        style={styles.root}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        {/* dark backdrop: tap outside the sheet to close */}
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />

        <View
          style={[
            styles.sheet,
            { paddingBottom: Math.max(insets.bottom, spacing.md) + spacing.sm },
          ]}>
          <View style={styles.handle} />

          {/* Header */}
          <View style={styles.headerRow}>
            <View style={styles.headerLabel}>
              <View style={styles.liveDot} />
              <Text style={styles.headerText}>
                {t('onboarding.city.header', 'MANUAL STATION RADAR')}
              </Text>
            </View>
            <Pressable onPress={onClose} hitSlop={10} style={styles.closeBtn}>
              <Text style={styles.closeText}>✕</Text>
            </Pressable>
          </View>

          <Text style={styles.title}>
            {t('onboarding.city.title', 'Select your city or region')}
          </Text>
          <Text style={styles.subtitle}>
            {t(
              'onboarding.city.subtitle',
              'Enter your city or postal code to find live pump prices and stations near you.',
            )}
          </Text>

          {/* Search bar */}
          <View style={styles.searchBar}>
            <AppIcon name="search" size={16} color={colors.textMuted} />
            <TextInput
              value={query}
              onChangeText={setQuery}
              placeholder={t('onboarding.city.placeholder', 'Search city')}
              placeholderTextColor={colors.textSubtle}
              style={styles.input}
              autoCorrect={false}
              autoCapitalize="words"
              returnKeyType="search"
            />
            {query.length > 0 && (
              <Pressable
                onPress={() => setQuery('')}
                hitSlop={8}
                style={styles.clearBtn}>
                <Text style={styles.clearText}>✕</Text>
              </Pressable>
            )}
            <View style={styles.divider} />
            <Pressable onPress={onUseGps} hitSlop={6} style={styles.gpsBtn}>
              <Text style={styles.gpsText}>◎ GPS</Text>
            </Pressable>
          </View>

          {/* List header */}
          <View style={styles.listHeader}>
            <View style={styles.listHeaderLeft}>
              <Text style={styles.listLabel}>
                {typing
                  ? t('onboarding.city.results', 'SEARCH RESULTS')
                  : t('onboarding.city.suggested', 'SUGGESTED & POPULAR')}
              </Text>
              {busy && !showSkeleton && (
                <ActivityIndicator size="small" color={colors.textSubtle} />
              )}
            </View>
            <View style={styles.regionPill}>
              <Text style={styles.regionText}>{CITY_LIST_LABEL}</Text>
            </View>
          </View>

          {/* Results */}
          <ScrollView
            style={styles.list}
            contentContainerStyle={styles.listContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}>
            {showSkeleton && (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            )}

            {showError && (
              <View style={styles.errorBox}>
                <Text style={styles.emptyTitle}>
                  {t('onboarding.city.loadError', 'Could not load cities')}
                </Text>
                <Text style={styles.emptyBody}>
                  {api.message ||
                    t(
                      'onboarding.city.loadErrorBody',
                      'Check your connection and try again. You can still search by name or use GPS.',
                    )}
                </Text>
                <Pressable
                  onPress={() => loadCities(true)}
                  style={styles.retryBtn}
                  accessibilityRole="button">
                  <Text style={styles.retryText}>
                    {t('onboarding.city.retry', 'Try again')}
                  </Text>
                </Pressable>
              </View>
            )}

            {cities.map(c => {
              const isSelected = c.id === selectedId;
              return (
                <SelectCard
                  key={c.id}
                  selected={isSelected}
                  indicator="none"
                  onPress={() => setSelectedId(c.id)}
                  style={styles.cityCard}>
                  <View style={styles.cityRow}>
                    <View
                      style={[styles.cityIcon, isSelected && styles.cityIconOn]}>
                      <PinIcon
                        color={isSelected ? colors.onPrimary : colors.textSubtle}
                        dot={isSelected ? colors.primary : colors.surfaceMuted}
                      />
                    </View>
                    <View style={styles.cityText}>
                      <Text style={styles.cityName}>{c.name}</Text>
                      <Text style={styles.cityMeta}>
                        {c.region}
                        {c.region && c.stationsActive != null ? ' • ' : ''}
                        {c.stationsActive != null
                          ? t(
                            'onboarding.city.stationsActive',
                            '{{n}} stations active',
                            { n: c.stationsActive },
                          )
                          : ''}
                      </Text>
                    </View>
                  </View>
                </SelectCard>
              );
            })}

            {showEmpty && (
              <View style={styles.empty}>
                <Text style={styles.emptyTitle}>
                  {t('onboarding.city.noResults', 'No city found')}
                </Text>
                <Text style={styles.emptyBody}>
                  {t(
                    'onboarding.city.noResultsBody',
                    'Try another spelling, or use GPS instead.',
                  )}
                </Text>
              </View>
            )}
          </ScrollView>

          {/* Confirm */}
          <PrimaryButton
            title={
              selected
                ? t('onboarding.city.view', 'View Stations in {{city}} →', {
                  city: selected.name,
                })
                : t('onboarding.city.choose', 'Select a city')
            }
            onPress={() => selected && onSelectCity(selected)}
          />
          <View style={styles.privacyRow}>
            <AppIcon name="lock" size={13} color={colors.textMuted} />
            <Text style={styles.privacy}>
              {t(
                'onboarding.city.privacy',
                'We only use your city to fetch local station fuel rates.',
              )}
            </Text>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  root: { flex: 1, justifyContent: 'flex-end', backgroundColor: colors.overlay },
  sheet: {
    backgroundColor: colors.background,
    borderTopLeftRadius: radii.sheet,
    borderTopRightRadius: radii.sheet,
    paddingHorizontal: spacing.margin + 4,
    paddingTop: spacing.sm,
    maxHeight: '90%',
    gap: spacing.md,
  },
  handle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.border,
  },

  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLabel: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.secondary,
  },
  headerText: { ...typography.labelSm, color: colors.primary },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.surfaceMuted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeText: { fontSize: 13, color: colors.textMuted },

  title: { ...typography.headlineLg, color: colors.primary },
  subtitle: { ...typography.bodySm, color: colors.textMuted, lineHeight: 17 },

  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radii.pill,
    paddingLeft: spacing.md + 2,
    paddingRight: spacing.sm,
    height: 52,
    gap: spacing.sm,
  },
  input: {
    flex: 1,
    ...typography.bodyLg,
    color: colors.text,
    paddingVertical: 0,
  },
  clearBtn: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearText: { fontSize: 10, color: colors.textMuted },
  divider: { width: 1, height: 22, backgroundColor: colors.border },
  gpsBtn: {
    backgroundColor: colors.mintSoft,
    borderRadius: radii.pill,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs + 3,
  },
  gpsText: { ...typography.labelMd, color: colors.primary },

  listHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  listHeaderLeft: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  listLabel: { ...typography.labelSm, color: colors.textSubtle },
  regionPill: {
    backgroundColor: colors.surfaceMuted,
    borderRadius: radii.pill,
    paddingHorizontal: spacing.sm + 2,
    paddingVertical: 3,
  },
  regionText: { ...typography.labelSm, color: colors.textMuted },

  list: { flexShrink: 1 },
  listContent: { gap: spacing.sm + 2, paddingVertical: 2 },
  cityCard: { padding: spacing.md },
  cityRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  cityIcon: {
    width: 40,
    height: 40,
    borderRadius: radii.sm,
    backgroundColor: colors.surfaceMuted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cityIconOn: { backgroundColor: colors.primary },
  cityText: { flex: 1, gap: 2 },
  cityName: { ...typography.labelLg, color: colors.text },
  cityMeta: { ...typography.bodySm, color: colors.textMuted },

  // the pin: a rounded square rotated 45°, with a dot inside
  pin: {
    width: 16,
    height: 16,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    transform: [{ rotate: '45deg' }],
    alignItems: 'center',
    justifyContent: 'center',
  },
  pinDot: { width: 6, height: 6, borderRadius: 3 },

  // loading placeholders
  skeletonCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.card,
    borderWidth: 1.5,
    borderColor: colors.borderSubtle,
    backgroundColor: colors.surface,
  },
  skeletonIcon: {
    width: 40,
    height: 40,
    borderRadius: radii.sm,
    backgroundColor: colors.surfaceMuted,
  },
  skeletonText: { flex: 1, gap: 8 },
  skeletonLine: {
    height: 12,
    borderRadius: 6,
    backgroundColor: colors.surfaceMuted,
  },

  // error / empty
  errorBox: {
    alignItems: 'center',
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.md,
    gap: spacing.sm,
    borderRadius: radii.card,
    backgroundColor: colors.errorTint,
    borderWidth: 1,
    borderColor: colors.errorContainer,
  },
  retryBtn: {
    marginTop: spacing.xs,
    backgroundColor: colors.primary,
    borderRadius: radii.pill,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  retryText: { ...typography.labelMd, color: colors.onPrimary },
  empty: { alignItems: 'center', paddingVertical: spacing.lg, gap: spacing.xs },
  emptyTitle: { ...typography.labelLg, color: colors.primary },
  emptyBody: {
    ...typography.bodySm,
    color: colors.textMuted,
    textAlign: 'center',
  },

  privacyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  privacy: {
    ...typography.bodySm,
    color: colors.textMuted,
    textAlign: 'center',
    flexShrink: 1,
  },
});

export default CitySearchSheet;